// WAY1 way tiles + the slim client ways doc (R0, APP_REARCH §3d "Ways";
// APP_PAVE "WAY1"; PROTOCOL.md "Way tiles"). PURE, three-free, CommonJS:
// the bake (pipeline/waytiles.mjs, through client/src/waybuild.js), the
// client (client/src/ways2.js) and the gate (scripts/check-way1.mjs) share
// this one reader / writer.
//
// A WAY1 tile is everything the client used to derive from ways.json with
// the ribbon pass (game/waysdraw.js drawnRibbons / paintParts / forkDraw /
// deck ribbons / laneMarks / nearDetail), baked per RIBBON_CHUNK (512 m)
// tile of the client's ribbon lattice:
//   meshes   the ribbon draw groups, one per (view class, material) of the
//            tile: ready vertex attributes (f32 / u8) + index (u16 / u32), as
//            ways2.js uploads them. A quad belongs to the tile of its
//            midpoint; a strip crossing a border repeats its boundary section
//            in both tiles (bit-identical vertices: no crack, no quad drawn
//            twice)
//   rails    the rail-head + frog instance matrices per view class (f32 x 16;
//            a kept head segment belongs to the tile of its midpoint, a frog
//            to the tile of its point)
//   detail   the near sleeper / kerb records (nearDetail: f32 x 7 / x 10) in
//            the tile of their point
//   pieces   the drawn pieces' OUTLINES (sections a / b), each piece WHOLE in
//            the tile of its first quad's midpoint (never drawn: the road
//            mask, the ground flights, the surface label and objects.js
//            cutWells read them), x / y / z quantized to Q_M (1 cm): the
//            first section absolute (i32), every next value a delta (i16)
//
// Layout (little-endian): 'WAY1' · u32 version · u32 header bytes · header
// JSON (utf8) · zero pad to 8 · the blobs, each 8-aligned. The header names
// the tile, the counts and every blob {type, off, len} (len in elements).
'use strict';

const { RIBBON_CHUNK } = require('./lodmath.js');

const MAGIC = 'WAY1';
const VERSION = 1;
const TILE_M = RIBBON_CHUNK;
const Q_M = 0.01;
const DIR = 'way1';
const NO_LABEL = 0xffff;
// piece flags
const F_RAILS = 1, F_FLIGHT = 2, F_GROUND = 4;
// the rail instances' unit (client/src/ways2.js railUnit, a 14-tri rail
// section): its profile and the head's lift over the ballast bed (roads3d
// LIFT.rail + 0.13) — the matrices are baked against these
const RAIL_LIFT = 0.13;
const RAIL_PROFILE = { head_w: 0.072, head_h: 0.04, web_w: 0.016, foot_w: 0.15, foot_h: 0.012 };

const TYPES = { f32: Float32Array, u8: Uint8Array, u16: Uint16Array, u32: Uint32Array, i16: Int16Array, i32: Int32Array };
const typeOf = (a) => {
  for (const [k, T] of Object.entries(TYPES)) if (a instanceof T) return k;
  throw new Error('WAY1: not a typed array of ' + Object.keys(TYPES).join(' / '));
};

const tileKey = (tx, tz) => tx + '_' + tz;
const tileOf = (x, z) => [Math.floor(x / TILE_M), Math.floor(z / TILE_M)];
const tileFile = (tx, tz) => `${DIR}/${tx}_${tz}.bin`;

// tile {tx, tz, labels, meshes, rails, detail, pieces} (typed arrays) -> Uint8Array
function encodeTile(t) {
  const blobs = [];
  const ref = (a) => { blobs.push(a); return { type: typeOf(a), i: blobs.length - 1, len: a.length }; };
  const P = t.pieces;
  const head = {
    tile: [t.tx, t.tz], tile_m: TILE_M, q_m: Q_M,
    counts: {
      meshes: t.meshes.length, rails: t.rails.length,
      instances: t.rails.reduce((s, r) => s + r.matrices.length / 16, 0),
      sleepers: t.detail.sleepers.length / 7, kerbs: t.detail.kerbs.length / 10,
      pieces: P.pid.length, sections: P.nsec.reduce((s, n) => s + n, 0),
    },
    labels: t.labels,
    meshes: t.meshes.map((m) => ({
      view: m.view, mat: m.mat, painted: m.painted,
      attrs: m.attrs.map((a) => ({ name: a.name, size: a.size, norm: !!a.norm, array: ref(a.array) })),
      index: ref(m.index),
    })),
    rails: t.rails.map((r) => ({ view: r.view, matrices: ref(r.matrices) })),
    detail: { sleepers: ref(t.detail.sleepers), kerbs: ref(t.detail.kerbs) },
    pieces: Object.fromEntries(['pid', 'run', 'flags', 'label', 'nsec', 'first', 'delta'].map((k) => [k, ref(P[k])])),
  };
  // blob offsets: after the header, each 8-aligned; the offsets lengthen
  // the header they are written in, so settle the start (it only grows)
  const pad8 = (n) => (n + 7) & ~7;
  const offs = blobs.map(() => 0);
  const fix = (v) => {
    if (Array.isArray(v)) return v.map(fix);
    if (v && typeof v === 'object') {
      if (Number.isInteger(v.i) && typeof v.type === 'string' && TYPES[v.type]) return { type: v.type, off: offs[v.i], len: v.len };
      return Object.fromEntries(Object.entries(v).map(([k, x]) => [k, fix(x)]));
    }
    return v;
  };
  const utf8 = new TextEncoder();
  let hb = utf8.encode(JSON.stringify(fix(head))), start = pad8(12 + hb.length), end = start;
  for (;;) {
    end = start;
    for (let k = 0; k < blobs.length; k++) { offs[k] = end; end = pad8(end + blobs[k].byteLength); }
    hb = utf8.encode(JSON.stringify(fix(head)));
    const s2 = pad8(12 + hb.length);
    if (s2 <= start) break;
    start = s2;
  }
  const out = new Uint8Array(end);
  for (let k = 0; k < 4; k++) out[k] = MAGIC.charCodeAt(k);
  const dv = new DataView(out.buffer);
  dv.setUint32(4, VERSION, true);
  dv.setUint32(8, hb.length, true);
  out.set(hb, 12);
  blobs.forEach((a, k) => out.set(new Uint8Array(a.buffer, a.byteOffset, a.byteLength), offs[k]));
  return out;
}

// ArrayBuffer | Uint8Array -> the header with every blob as a typed-array
// VIEW on the bytes (no copy when 8-aligned). Throws on a wrong magic /
// version / a blob outside the file.
function decodeTile(src) {
  let u8 = src instanceof Uint8Array ? src : new Uint8Array(src);
  if (u8.byteOffset & 7) u8 = u8.slice();
  if (u8.length < 12 || String.fromCharCode(u8[0], u8[1], u8[2], u8[3]) !== MAGIC) throw new Error('WAY1: bad magic');
  const dv = new DataView(u8.buffer, u8.byteOffset, u8.byteLength);
  const ver = dv.getUint32(4, true), hl = dv.getUint32(8, true);
  if (ver !== VERSION) throw new Error(`WAY1: version ${ver}, this reader reads ${VERSION}`);
  if (12 + hl > u8.length) throw new Error('WAY1: header past the end');
  const head = JSON.parse(new TextDecoder().decode(u8.subarray(12, 12 + hl)));
  const view = (v) => {
    if (Array.isArray(v)) return v.map(view);
    if (v && typeof v === 'object') {
      if (typeof v.type === 'string' && TYPES[v.type] && Number.isInteger(v.off) && Number.isInteger(v.len) && Object.keys(v).length === 3) {
        const T = TYPES[v.type];
        if (v.off % T.BYTES_PER_ELEMENT || v.off + v.len * T.BYTES_PER_ELEMENT > u8.length) throw new Error(`WAY1: blob ${v.type}@${v.off}x${v.len} outside the file`);
        return new T(u8.buffer, u8.byteOffset + v.off, v.len);
      }
      return Object.fromEntries(Object.entries(v).map(([k, x]) => [k, view(x)]));
    }
    return v;
  };
  const t = view(head);
  t.version = ver;
  [t.tx, t.tz] = t.tile;
  return t;
}

// the outline coder: pieces [{pid, run, flags, label, sec: [{a, b}]}] ->
// the tile's typed arrays (throws on a step past i16 at Q_M)
function encodePieces(list) {
  const n = list.length;
  const pid = new Uint32Array(n), run = new Uint32Array(n), flags = new Uint8Array(n), label = new Uint16Array(n), nsec = new Uint32Array(n);
  const first = new Int32Array(3 * n);
  let nd = 0;
  for (const p of list) nd += 6 * p.sec.length - 3;
  const delta = new Int16Array(nd);
  const q = (v) => Math.round(v / Q_M);
  let o = 0;
  list.forEach((p, k) => {
    pid[k] = p.pid; run[k] = p.run; flags[k] = p.flags; label[k] = p.label; nsec[k] = p.sec.length;
    const S = p.sec;
    let px = 0, py = 0, pz = 0;
    const put = (d) => {
      if (d < -32768 || d > 32767) throw new Error(`WAY1 outline: piece ${p.pid} (run ${p.run}) steps ${d * Q_M} m between two section points (> i16 at ${Q_M} m)`);
      delta[o++] = d;
    };
    for (let i = 0; i < S.length; i++) {
      const ax = q(S[i].a[0]), ay = q(S[i].a[1]), az = q(S[i].a[2]);
      if (i === 0) { first[3 * k] = ax; first[3 * k + 1] = ay; first[3 * k + 2] = az; } else { put(ax - px); put(ay - py); put(az - pz); }
      put(q(S[i].b[0]) - ax); put(q(S[i].b[1]) - ay); put(q(S[i].b[2]) - az);
      px = ax; py = ay; pz = az;
    }
  });
  return { pid, run, flags, label, nsec, first, delta };
}

// a decoded tile's pieces -> [{pid, run, rails, flight, ground, label, sec: [{a, b}]}]
// (metres = the cm integers x Q_M; labels from the tile's table)
function decodePieces(t) {
  const P = t.pieces, out = [];
  let o = 0;
  for (let k = 0; k < P.pid.length; k++) {
    let ax = P.first[3 * k], ay = P.first[3 * k + 1], az = P.first[3 * k + 2];
    const sec = new Array(P.nsec[k]);
    for (let i = 0; i < sec.length; i++) {
      if (i > 0) { ax += P.delta[o++]; ay += P.delta[o++]; az += P.delta[o++]; }
      const bx = ax + P.delta[o++], by = ay + P.delta[o++], bz = az + P.delta[o++];
      sec[i] = { a: [ax * Q_M, ay * Q_M, az * Q_M], b: [bx * Q_M, by * Q_M, bz * Q_M] };
    }
    const f = P.flags[k];
    out.push({ pid: P.pid[k], run: P.run[k], rails: !!(f & F_RAILS), flight: !!(f & F_FLIGHT), ground: (f & F_GROUND) ? 1 : 0,
      label: P.label[k] === NO_LABEL ? null : t.labels[P.label[k]], sec });
  }
  return out;
}

// ------------------------------------------------------------ the slim doc
// THE CLIENT'S ways.json: what the client's remaining readers read of it,
// declared here (check-way1 proves each reader's output equal on the slim
// and the full doc, on its fixture and on the live bake). ways.json itself
// is the server sim's and the gates' (private). Kept WHOLE: the `top` keys,
// corridor's `corridor` keys, and per run the `run` keys (in the source
// order). The readers and what they take:
//   graphSteps (waysdraw: bike RoadGraph, the Map's early streets)   ways[].cls name x y z kind
//   ways2 _mapData (bench facing)                                    ways[].paved
//   buildTrailIndex (trailmarks: trail index, marks, inspect, HUD)  ways[].cls chain way name pcls sac tt w x y z kind
//   ways2 _bores (bore / site / deck carve, corridor.js)            bores, sites, decks
//   platformsIndex (game/pave.js: the grid's platforms, labels)     platforms
//   structplan railRuns / boreFrames (structure rails, lamp frames) ways[].cls pcls path gauge_m x y z kind flag,
//                                                                    codes, corridor.classes, forks, decks, bores, ballast
//   grass deckCover (no scatter under a deck)                       decks
// (the drawn pieces' flight flag and surface label are baked into the tiles)
const SLIM = {
  rev: 1,
  top: ['format', 'schema', 'region', 'codes', 'ballast', 'bores', 'decks', 'sites', 'forks', 'platforms'],
  corridor: ['classes'],
  run: ['cls', 'pcls', 'name', 'chain', 'path', 'way', 'sac', 'tt', 'w', 'paved', 'gauge_m', 'x', 'y', 'z', 'kind', 'flag'],
};
const SLIM_FILE = 'ways_slim.json';

// the full doc -> the slim doc (the declared keys only, source order; the
// values are the full doc's own, never re-derived)
function slimWays(doc) {
  const out = {};
  const top = new Set(SLIM.top), run = new Set(SLIM.run), cor = new Set(SLIM.corridor);
  for (const k of Object.keys(doc)) {
    if (top.has(k)) out[k] = doc[k];
    else if (k === 'corridor' && doc.corridor && typeof doc.corridor === 'object') {
      out.corridor = {};
      for (const c of Object.keys(doc.corridor)) if (cor.has(c)) out.corridor[c] = doc.corridor[c];
    } else if (k === 'ways') {
      out.ways = doc.ways.map((e) => {
        const r = {};
        for (const f of Object.keys(e)) if (run.has(f)) r[f] = e[f];
        return r;
      });
    }
  }
  out.slim = { rev: SLIM.rev, of: 'ways.json' };
  return out;
}

module.exports = { MAGIC, VERSION, TILE_M, Q_M, DIR, NO_LABEL, F_RAILS, F_FLIGHT, F_GROUND, RAIL_LIFT, RAIL_PROFILE, SLIM, SLIM_FILE,
  tileKey, tileOf, tileFile, encodeTile, decodeTile, encodePieces, decodePieces, slimWays };
